/**
 * Pure, framework-agnostic helpers for the Hub chat surface. They stay in the
 * browser-shared layer so the React `ChatSurface` and compiled tests use the
 * same logic without requiring a DOM.
 */
import { HUB_CHAT_PLATFORM } from "@lifter/agent-runtime-contracts/channel-platforms";

/**
 * Platform tag the gateway stamps on conversations a Hub user started in-app.
 *
 * Re-exported from the neutral contracts package rather than restated: the
 * hub-chat adapter WRITES this tag into the transcript envelope and this module
 * READS it back, so a second spelling would type-check and disagree in silence.
 * Kept on the browser-shared surface so SPA call sites keep one import.
 */
export { HUB_CHAT_PLATFORM };

/**
 * Authenticated attachment URL carrying both the conversation owner and the
 * durable workspace path plus short-lived staged fallback when available.
 */
export function attachmentDownloadUrl(
  agentId: string,
  sessionId: string,
  attachment: { objectKey?: string; workspacePath?: string },
): string | undefined {
  if (!attachment.workspacePath && !attachment.objectKey) return undefined;
  const params = new URLSearchParams({ sessionId });
  if (attachment.workspacePath) params.set("workspacePath", attachment.workspacePath);
  if (attachment.objectKey) params.set("objectKey", attachment.objectKey);
  return `/api/agents/${encodeURIComponent(agentId)}/chat/attachments/download?${params.toString()}`;
}

/**
 * Whether a past conversation can be re-opened *live* and continued in the same
 * session, versus shown read-only. Only Hub-initiated conversations resume:
 * other channels (Slack, etc.) lack an in-Hub composer round-trip, so revisiting
 * them stays a transcript. Keyed off the gateway-stamped `platform`.
 */
export function isResumableLivePlatform(platform: string): boolean {
  return platform === HUB_CHAT_PLATFORM;
}

/**
 * Whether a set of message timestamps spans more than one calendar day. When
 * it does, hover timestamps include the date; within a single day they show
 * the time alone. Non-parseable timestamps are ignored so a single bad value
 * never forces every bubble into the verbose form.
 */
export function spansMultipleDays(timestamps: readonly string[]): boolean {
  const days = new Set<string>();
  for (const ts of timestamps) {
    const date = new Date(ts);
    if (Number.isNaN(date.getTime())) continue;
    days.add(`${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`);
    if (days.size > 1) return true;
  }
  return false;
}

/**
 * Short local timestamp for a bubble's hover chip. `withDate` adds the
 * calendar date — used when the conversation spans multiple days so a reader
 * can tell "9:05 today" from "9:05 last Tuesday". Returns an empty string for
 * an unparseable value so the chip simply renders nothing rather than `NaN`.
 */
export function formatBubbleTimestamp(value: string | number | Date, withDate: boolean): string {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const time = date.toLocaleTimeString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
  if (!withDate) return time;
  const month = date.toLocaleDateString("en-US", { month: "short" });
  const dayNum = date.getDate();
  const year = date.getFullYear();
  return `${time}, ${month} ${dayNum} ${year}`;
}

/**
 * Whether a replayed user bubble should carry a "via <channel>" attribution
 * caption. It does when the sender is not the viewer reading the transcript:
 * either the message came from a non-Hub platform (Slack, etc.) or, within
 * Hub, from a different user id. A message with no structured participant is
 * treated as the viewer's own — unattributed Hub turns are the viewer's.
 */
export function shouldAttributeParticipant(
  participant: { platform: string; userId: string } | undefined,
  viewerUserId: string | undefined,
): boolean {
  if (!participant) return false;
  // The gateway stamps the channel's own id, so the Hub's own conversations
  // must be compared against `HUB_CHAT_PLATFORM` — a literal that does not
  // match it attributes every Hub message, including the reader's own, and
  // does so only on the path that has an envelope to read (a reload).
  if (participant.platform !== HUB_CHAT_PLATFORM) return true;
  return viewerUserId !== undefined && participant.userId !== viewerUserId;
}

/**
 * Outcome line for a decided-approval record row ("Approved by Jane · 14:32").
 * Human terminals name the decider when a display name was captured; expiry
 * and cancellation use the same copy as the live card's cancelled states. The
 * time label is appended when non-empty (an unparseable resolvedAt yields
 * none rather than `NaN`).
 */
export function approvalRecordLabel(
  state: "approved" | "denied" | "expired" | "cancelled",
  resolvedByName: string | undefined,
  timeLabel: string,
): string {
  const by = resolvedByName ? ` by ${resolvedByName}` : "";
  const outcome =
    state === "approved"
      ? `Approved${by}`
      : state === "denied"
        ? `Denied${by}`
        : state === "expired"
          ? "Timed out — no decision in time"
          : "Cancelled — the turn ended";
  return timeLabel ? `${outcome} · ${timeLabel}` : outcome;
}

/**
 * Compact duration label for a tool-call chip: one decimal under ten seconds
 * ("3.2s"), whole seconds under a minute ("42s"), minutes + zero-padded
 * seconds beyond ("2m 05s"). Negative input clamps to zero — client/server
 * clock skew must never render a negative duration.
 */
export function formatToolElapsed(elapsedMs: number): string {
  const totalSeconds = Math.max(0, elapsedMs) / 1000;
  if (totalSeconds < 10) return `${totalSeconds.toFixed(1)}s`;
  const rounded = Math.round(totalSeconds);
  if (rounded < 60) return `${rounded}s`;
  const minutes = Math.floor(rounded / 60);
  const seconds = rounded - minutes * 60;
  return `${minutes}m ${String(seconds).padStart(2, "0")}s`;
}

/**
 * Whether a date banner belongs between two consecutive replay turns. The
 * Slack/iMessage pattern: a banner appears only when the gap is large enough
 * that the reader has lost the thread of "when" — here, more than an hour.
 * Unparseable timestamps never trigger a banner (we cannot place it).
 */
const TURN_GAP_BANNER_MS = 60 * 60 * 1000;

export function shouldShowDateBanner(previousTs: string, currentTs: string): boolean {
  const prev = new Date(previousTs).getTime();
  const curr = new Date(currentTs).getTime();
  if (Number.isNaN(prev) || Number.isNaN(curr)) return false;
  return Math.abs(curr - prev) > TURN_GAP_BANNER_MS;
}
